import { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import { ReviewWriteMobileForm } from './ReviewWriteMobileForm';
import ChevronIcon from '../../../assets/icons/ChevronIcon';
import { CompleteView } from './common/CompleteView';
import BottomSheet from './common/BottomSheet';
import { COLORS, FONT_SIZES, FONT_WEIGHTS, SPACING, Z_INDEX, BREAKPOINTS } from './common/constants';
import { ProductApi } from '../../../api/productApi';
import { manageBodyOverflow, cleanupBodyOverflow, handleModalClose } from '../utils/modalUtils';
import { isFormValid } from '../utils/validationUtils';
import { hasFormData } from '../utils/formUtils';
import { transformTaxonomyData } from '../utils/taxonomyUtils';
import type { MobileReviewModalProps, MobileReviewModalValues } from './common/interfaces';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${Z_INDEX.mobileModal};
  background: ${COLORS.background};
  display: flex;
  flex-direction: column;
  @media (min-width: ${BREAKPOINTS.mobile}) {
    display: none;
  }
`;

const Header = styled.div`
  height: 66px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  background: ${COLORS.background};
  padding: ${SPACING.xl} ${SPACING.lg} ${SPACING.sm} ${SPACING.lg};
  box-sizing: border-box;
  gap: ${SPACING.lg};
`;

const Title = styled.div`
  color: ${COLORS.text};
  font-feature-settings: 'ss10' on;
  font-family: 'Plus Jakarta Sans';
  font-size: ${FONT_SIZES['2xl']};
  font-style: normal;
  font-weight: ${FONT_WEIGHTS.normal};
  line-height: 140%;
`;

const CloseButton = styled.button`
  border: none !important;
  font-size: 28px !important;
  color: #333 !important;
  cursor: pointer !important;
  border-radius: 30px;
  padding: ${SPACING.sm};
  background: revert !important;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  background: ${COLORS.background};
  padding: ${SPACING.lg};
`;

export const ReviewWriteMobileModal = ({
  isOpen,
  productImage,
  onClose,
  onSubmit,
  productId,
}: MobileReviewModalProps) => {
  // Form state
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [nickname, setNickname] = useState('');
  const [skinType, setSkinType] = useState('');
  const [skinConcern, setSkinConcern] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [recommend, setRecommend] = useState('');
  const [isVerified, setIsVerified] = useState(false);

  // Modal state
  const [isComplete, setIsComplete] = useState(false);

  // Taxonomy data query
  const { data: taxonomy } = useQuery({
    queryKey: ['taxonomy'],
    queryFn: ProductApi.getTaxonomy,
    enabled: isOpen,
  });

  const { skinTypeOptions, skinConcernOptions } = transformTaxonomyData(taxonomy);

  // Get current form state
  const getCurrentFormState = useCallback((): MobileReviewModalValues => {
    return {
      rating,
      title,
      content,
      nickname,
      skinType,
      skinConcern,
      images,
      recommend,
      isVerified,
    };
  }, [rating, title, content, nickname, skinType, skinConcern, images, recommend, isVerified]);

  // Derived state
  const formState = getCurrentFormState();
  const hasUnsavedChanges = hasFormData(formState);
  const isSubmitDisabled = !isFormValid(formState);

  // Reset form
  const resetForm = useCallback(() => {
    setRating(0);
    setTitle('');
    setContent('');
    setNickname('');
    setSkinType('');
    setSkinConcern('');
    setImages([]);
    setRecommend('');
    setIsVerified(false);
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    try {
      await onSubmit(formState);
      setIsComplete(true);
    } catch (error) {
      console.error('Form submission failed:', error);
      // Handle error appropriately
    }
  }, [onSubmit, formState]);

  // Handle modal close
  const handleClose = useCallback(() => {
    handleModalClose({
      hasUnsavedChanges,
      isComplete,
      onShowConfirmation: () => {
        // For mobile, we'll just close directly without confirmation
        resetForm();
        setIsComplete(false);
        onClose();
      },
      onClose: () => {
        resetForm();
        setIsComplete(false);
        onClose();
      },
    });
  }, [hasUnsavedChanges, isComplete, onClose, resetForm]);

  // Manage body overflow
  useEffect(() => {
    manageBodyOverflow(isOpen);
    return () => {
      cleanupBodyOverflow();
    };
  }, [isOpen]);

  // Clear form data when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setIsComplete(false);
    }
  }, [isOpen, resetForm]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {!isComplete && (
        <Overlay>
          <Header>
            <CloseButton onClick={handleClose}>
              <ChevronIcon direction="left" />
            </CloseButton>
            <Title>Review this product</Title>
          </Header>

          <ScrollArea>
            <ReviewWriteMobileForm
              rating={rating}
              setRating={setRating}
              title={title}
              setTitle={setTitle}
              content={content}
              setContent={setContent}
              nickname={nickname}
              setNickname={setNickname}
              skinType={skinType}
              setSkinType={setSkinType}
              skinConcern={skinConcern}
              setSkinConcern={setSkinConcern}
              images={images}
              setImages={setImages}
              productImage={productImage}
              recommend={recommend}
              setRecommend={setRecommend}
              isVerified={isVerified}
              setIsVerified={setIsVerified}
              isSubmitDisabled={isSubmitDisabled}
              onSubmit={handleSubmit}
              skinTypeOptions={skinTypeOptions}
              skinConcernOptions={skinConcernOptions}
            />
          </ScrollArea>
        </Overlay>
      )}

      {isComplete && (
        <BottomSheet isOpen={isComplete} onClose={onClose} title="">
          <CompleteView onContinue={onClose} variant="mobile" />
        </BottomSheet>
      )}
    </>
  );
};
